import React, { useState } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  Package,
  User,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Edit,
  Save,
  X,
  Mail,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700"
};

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  in_progress: "Em Andamento",
  delivered: "Entregue",
  cancelled: "Cancelado"
};

interface ServiceData {
  id: string;
  item_id: string;
  contact_id: string;
  service_type: string | null;
  status: string | null;
  assigned_to_user_id: string | null;
  delivery_date: string | null;
  completion_date: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  item?: {
    id: string;
    unit_price: number | null;
    total_amount: number | null;
    catalog?: {
      id: string;
      name: string;
      description: string | null;
    };
    deal?: {
      id: string;
      name: string;
      source: string | null;
    };
  };
  contact?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
  };
  assigned_user?: {
    id: string;
    display_name: string | null;
  };
}

export default function ServiceDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("id");

  const [activeTab, setActiveTab] = useState<"overview" | "notes" | "tasks" | "documents">("overview");
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState("");

  // Fetch service data
  const { data: service, isLoading, error } = useQuery<ServiceData>({
    queryKey: ["service", serviceId],
    queryFn: async () => {
      if (!serviceId) throw new Error("Service ID not provided");

      const { data, error } = await supabase
        .from("services")
        .select(`
          *,
          item:items(
            id,
            unit_price,
            total_amount,
            catalog:catalog(id, name, description),
            deal:deals(id, name, source)
          ),
          contact:contacts(id, first_name, last_name, primary_email, primary_phone),
          assigned_user:app_users!services_assigned_to_user_id_fkey(id, display_name)
        `)
        .eq("id", serviceId)
        .single();

      if (error) throw error;
      return data as ServiceData;
    },
    enabled: !!serviceId
  });

  // Fetch users for assignment
  const { data: users } = useQuery({
    queryKey: ["app_users_active"],
    queryFn: async () => {
      const { data } = await supabase
        .from("app_users")
        .select("id, display_name")
        .eq("active", true)
        .order("display_name");
      return data || [];
    }
  });

  // Update service mutation
  const updateService = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      const { error } = await supabase
        .from("services")
        .update(updates)
        .eq("id", serviceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service", serviceId] });
      toast.success("Serviço atualizado com sucesso");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar serviço: " + error.message);
    }
  });

  const handleStatusChange = (newStatus: string) => {
    const updates: Partial<ServiceData> = { status: newStatus };
    if (newStatus === "delivered") {
      updates.completion_date = safeFormat(new Date(), "yyyy-MM-dd");
    }
    updateService.mutate(updates);
  };

  const handleAssigneeChange = (userId: string) => {
    updateService.mutate({ assigned_to_user_id: userId === "unassigned" ? null : userId });
  };

  const handleSaveNotes = () => {
    updateService.mutate({ notes: editedNotes });
    setIsEditingNotes(false);
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount);
  };

  const getServiceNumber = (id: string) => {
    if (!id) return "";
    const parts = id.split("-");
    if (parts.length >= 2) {
      return `#SVC-${parts[1].substring(0, 4).toUpperCase()}`;
    }
    return `#${id.substring(0, 8)}`;
  };

  // Loading state - matching CaseDetail
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  // Error state - matching CaseDetail
  if (error) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Serviços
          </button>
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">Erro: {(error as Error).message}</p>
            <Button onClick={() => navigate(-1)}>Voltar</Button>
          </div>
        </div>
      </div>
    );
  }

  // Not found state - matching CaseDetail
  if (!serviceId || !service) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Serviços
          </button>
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 mb-4">Serviço não encontrado</p>
            <Button onClick={() => navigate(-1)}>Voltar</Button>
          </div>
        </div>
      </div>
    );
  }

  const contactName = [service.contact?.first_name, service.contact?.last_name]
    .filter(Boolean)
    .join(" ") || "Cliente";

  return (
    <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button - matching CaseDetail */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Serviços
        </button>

        {/* Header - matching CaseDetail */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                {service.item?.catalog?.name || service.service_type || "Serviço"}
              </h1>
              {service.status && (
                <Badge className={`${statusColors[service.status] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>
                  {statusLabels[service.status] || service.status}
                </Badge>
              )}
            </div>
            <p className="text-slate-600 dark:text-slate-400">
              Serviço {getServiceNumber(service.id)} • Cliente: {contactName}
            </p>
          </div>
        </div>

        {/* Tabs - matching CaseDetail pill style */}
        <div className="flex gap-2 mb-8 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-full inline-flex">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-6 py-2.5 font-medium transition-all rounded-full ${
              activeTab === "overview"
                ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            }`}
          >
            Visão Geral
          </button>
          <button
            onClick={() => setActiveTab("notes")}
            className={`px-6 py-2.5 font-medium transition-all rounded-full ${
              activeTab === "notes"
                ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            }`}
          >
            Notas
          </button>
          <button
            onClick={() => setActiveTab("tasks")}
            className={`px-6 py-2.5 font-medium transition-all rounded-full ${
              activeTab === "tasks"
                ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            }`}
          >
            Tarefas
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`px-6 py-2.5 font-medium transition-all rounded-full ${
              activeTab === "documents"
                ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            }`}
          >
            Documentos
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left Column (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Service Details Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5" />
                    Detalhes do Serviço
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Tipo de Serviço</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {service.item?.catalog?.name || service.service_type || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Valor</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {formatCurrency(service.item?.unit_price || null)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Data de Criação</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {service.created_at
                          ? safeFormat(new Date(service.created_at), "dd/MM/yyyy", { locale: ptBR })
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Data de Conclusão</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {service.completion_date
                          ? safeFormat(new Date(service.completion_date), "dd/MM/yyyy", { locale: ptBR })
                          : "Não concluído"}
                      </p>
                    </div>
                  </div>
                  {service.item?.catalog?.description && (
                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Descrição</p>
                      <p className="text-slate-700 dark:text-slate-300">
                        {service.item.catalog.description}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Status & Assignment Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CheckCircle2 className="w-5 h-5" />
                    Status e Atribuição
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Status</p>
                      <Select value={service.status || "pending"} onValueChange={handleStatusChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="in_progress">Em Andamento</SelectItem>
                          <SelectItem value="delivered">Entregue</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Responsável</p>
                      <Select
                        value={service.assigned_to_user_id || "unassigned"}
                        onValueChange={handleAssigneeChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecionar responsável" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Não atribuído</SelectItem>
                          {users?.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.display_name || "Sem nome"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Right Column (1/3) */}
            <div className="space-y-6">
              {/* Customer Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5" />
                    Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {service.contact ? (
                    <>
                      <button
                        onClick={() => navigate(`/ContactDetail?id=${service.contact?.id}`)}
                        className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {contactName}
                      </button>
                      {service.contact.primary_email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Mail className="w-4 h-4" />
                          <span>{service.contact.primary_email}</span>
                        </div>
                      )}
                      {service.contact.primary_phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Phone className="w-4 h-4" />
                          <span>{service.contact.primary_phone}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400">Nenhum cliente vinculado</p>
                  )}
                </CardContent>
              </Card>

              {/* Deal Reference Card */}
              {service.item?.deal && (
                <Card className="border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5" />
                      Negócio
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <button
                      onClick={() => navigate(`/DealDetail?id=${service.item?.deal?.id}`)}
                      className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {service.item.deal.name}
                    </button>
                    {service.item.deal.source && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Origem: {service.item.deal.source === "direct" ? "Venda Direta" : service.item.deal.source}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Quick Info Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Clock className="w-5 h-5" />
                    Informações
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Criado em</span>
                    <span className="text-slate-900 dark:text-slate-100">
                      {service.created_at
                        ? safeFormat(new Date(service.created_at), "dd/MM/yyyy", { locale: ptBR })
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Responsável</span>
                    <span className="text-slate-900 dark:text-slate-100">
                      {service.assigned_user?.display_name || "Não atribuído"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === "notes" && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" />
                Notas e Observações
              </CardTitle>
              {!isEditingNotes && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditedNotes(service.notes || "");
                    setIsEditingNotes(true);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditingNotes ? (
                <div className="space-y-4">
                  <Textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    placeholder="Adicione notas sobre o serviço..."
                    className="min-h-[200px]"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleSaveNotes} size="sm">
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingNotes(false)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  {service.notes ? (
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {service.notes}
                    </p>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400 italic">
                      Nenhuma nota adicionada ainda.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "tasks" && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle2 className="w-5 h-5" />
                Tarefas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                <p className="text-slate-500 dark:text-slate-400">
                  Funcionalidade de tarefas em desenvolvimento
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "documents" && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" />
                Documentos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                <p className="text-slate-500 dark:text-slate-400">
                  Funcionalidade de documentos em desenvolvimento
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
